import { CommonModule } from "@angular/common";
import { Component, Input, effect, inject, signal } from "@angular/core";

import {
    EnvironmentInfoService,
    ToolbarEventsService
} from "src/app/core";

import { BookingListComponent } from "./components";
import { TilbyToolbar } from "src/app/models";
import { BookingTopbarComponent } from "./components/booking-topbar";
import { BookingsDataInput, BookingsStateService } from "./services/bookings.state.service";
import { TilbyDatePipe } from "@tilby/tilby-ui-lib/pipes/tilby-date";
import { BookingWeekComponent } from "./components/booking-week";
import { BookingTimelineComponent } from "./components/booking-timeline";
import { Bookings, BookingShifts, Rooms } from "tilby-models";
import { AddEditNewBookingDialogService } from "src/app/dialogs/bookings/add-edit-new-booking-dialog/add-edit-new-booking-dialog.component";
import { DownloadBookingDialogService } from "src/app/dialogs/bookings/download-booking-dialog/download-booking-dialog.component";
import { $stateParams } from "app/ajs-upgraded-providers";

@Component({
    selector: 'app-bookings',
    templateUrl: './bookings.component.html',
    styleUrls: ['./bookings.component.scss'],
    imports: [
        CommonModule,
        BookingListComponent,
        BookingWeekComponent,
        BookingTimelineComponent
    ],
    providers: [],
    standalone: true,
})
export class BookingsComponent implements TilbyToolbar {
    private readonly $stateParams = inject($stateParams);
    private readonly tilbyDatePipe = inject(TilbyDatePipe);
    private readonly bookingsStateService = inject(BookingsStateService);
    private readonly toolbarEventsService = inject(ToolbarEventsService);
    private readonly environmentInfoService = inject(EnvironmentInfoService);
    private readonly downloadBookingDialogService = inject(DownloadBookingDialogService);
    private readonly addEditBookingDialogService = inject(AddEditNewBookingDialogService);

    @Input() rooms: Rooms[] = [];
    @Input() bookings: Bookings[] = [];
    @Input() bookingShifts: BookingShifts[] = [];

    isMobile = signal(this.bookingsStateService.isMobile());

    data : BookingsDataInput = this.bookingsStateService.selectBookingsData();

    constructor() {
        effect(() => {
            this.data = this.bookingsStateService.selectBookingsData();
            if (this.bookingsStateService.selectBookingsData()) {
                this.createToolbarButtons();
            }
        });
    }

    ngOnInit(): void {
        this.setToolbar();
        this.createToolbarButtons();

        //set bookings into booking state service for every components
        if(this.bookings) {
            this.bookingsStateService.bookingData.set(this.bookings);
            this.bookingsStateService.bookings = this.bookings;
        }

        //get booking from Pienissimo
        if(this.$stateParams.booking){
            this.bookingsStateService.changeBookingDate(this.initDate(this.$stateParams.booking.booked_for));
        } else {
            this.bookingsStateService.changeBookingDate(this.initDate());
        }
    }

    initDate(customDate?: string): Date {
        const currentStringDate = this.tilbyDatePipe.transform(customDate ? customDate : TilbyDatePipe.date(), 'yyyy-MM-dd');
        let currentDate = TilbyDatePipe.date({outputFormat: 'date', date: currentStringDate || ''});
        currentDate.setUTCHours(0, 0, 0, 0);
        return currentDate;
    }

    createToolbarButtons(): void {
        this.toolbarEventsService.buttons$.next({
            barButtons: [
                {
                    isIt: signal(!this.isMobile() && this.data.type !== 1),
                    isAccessibility: signal(false),
                    name: 'add_edit_booking',
                    iconType: 'symbols',
                    icon: signal('calendar_add_on'),
                    click: () => this.addNewBooking()
                },
                {
                    isIt: signal(!this.isMobile() && this.bookingsStateService.selectBookingsData().type === 1 && this.environmentInfoService.canDownloadFiles()),
                    isAccessibility: signal(false),
                    name: 'download',
                    icon: signal('download'),
                    click: () => this.downloadBooking()
                }
            ],
            panelButtons: [
                {
                    isIt: signal(this.isMobile()),
                    name: 'view_week',
                    icon: signal('event'),
                    label: signal('BOOKINGS.TOPBAR_CALENDAR.TIMEGRID_WEEK'),
                    click: () => this.bookingsStateService.changeType(0)
                },
                {
                    isIt: signal(this.isMobile()),
                    name: 'view_list',
                    icon: signal('event'),
                    label: signal('BOOKINGS.TOPBAR_CALENDAR.LIST'),
                    click: () => this.bookingsStateService.changeType(1)
                },
                {
                    isIt: signal(this.isMobile()),
                    name: 'view_resource',
                    icon: signal('event'),
                    label: signal('BOOKINGS.TOPBAR_CALENDAR.TIMELINE'),
                    click: () => this.bookingsStateService.changeType(2)
                },
                {
                    isIt: signal(this.isMobile()),
                    name: 'add_edit_booking',
                    iconType: 'symbols',
                    icon: signal('calendar_add_on'),
                    label: signal('BOOKINGS.TOPBAR_CALENDAR.ADD_BOOKING'),
                    click: () => this.addNewBooking()
                },
                {
                    isIt: signal(this.isMobile() && this.bookingsStateService.selectBookingsData().type === 1 && this.environmentInfoService.canDownloadFiles()),
                    name: 'download',
                    icon: signal('download'),
                    label: signal('BOOKINGS.TOPBAR_CALENDAR.DOWNLOAD_BOOKINGS'),
                    click: () => this.downloadBooking()
                }
            ]
        });
    }

    private setToolbar() {
        this.setModuleTitle();
        this.toolbarEventsService.centralToolbarComponent$.next(BookingTopbarComponent);
    }

    private setModuleTitle() {
        this.toolbarEventsService.moduleTitle.next("BOOKINGS");
    }

    addNewBooking(){
        this.addEditBookingDialogService.openDialog({data: {bookings: this.bookings, bookingShifts: this.bookingShifts, rooms: this.rooms, options: []}})
        .then((result) => {
            if(result) {
                const resultDate = this.tilbyDatePipe.transform(TilbyDatePipe.date(result.booking.booked_for), 'yyyy-MM-dd');
                const bookingDate = TilbyDatePipe.date({outputFormat: 'date', date: resultDate || ''});
                
                this.bookingsStateService.addBooking(result.booking, bookingDate);
            }
        });
    }

    async downloadBooking() {
        const currentDate = this.bookingsStateService.selectBookingsData().date;
        const res = await this.downloadBookingDialogService.openDialog({currentDate: currentDate, rooms: this.rooms});

        if (!res || (res.result === '')) return;
    }
}
